import { authenticatedFetch, getAuthHeaders } from './authApi';
import { getEnv } from '../utils/env';

export type ResolvedShareLink = {
  accessMode: 'public' | 'secured';
  path: string;
  resourceType: string;
  resourceId?: string;
};

type ShareEnvelope = {
  ok?: boolean;
  data?: {
    value?: {
      accessMode?: 'public' | 'secured';
      path?: string;
      resourceType?: string;
      resourceId?: string;
    };
  };
};

type CreateShareEnvelope = ShareEnvelope & {
  data?: ShareEnvelope['data'] & { key?: string };
};

export const createTrainingShareLinkApi = async (params: {
  resourceType: 'exercise' | 'plan' | 'session';
  resourceId: string;
  accessCode?: string;
  shareKey?: string;
}): Promise<string> => {
  const baseUrl = getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');
  if (!baseUrl) throw new Error('VITE_AI_API_URL is not configured');
  const headers = getAuthHeaders({
    ...(params.accessCode ? { 'X-Training-Access-Code': params.accessCode } : {}),
    ...(params.shareKey ? { 'X-Training-Share-Key': params.shareKey } : {}),
  });
  const response = await authenticatedFetch(`${baseUrl}/api/v1/share-links`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({
      version: 1,
      path: `/training-library/${params.resourceType}s/${params.resourceId}`,
      fingerprint: 'server-derived',
      resourceType: params.resourceType,
      resourceId: params.resourceId,
      accessMode: 'public',
      securityContext: {},
      expiresInDays: 30,
    }),
  });
  const envelope = await response.json().catch(() => null) as CreateShareEnvelope | null;
  const key = envelope?.data?.key;
  if (!response.ok || envelope?.ok === false || !key) {
    throw new Error(`Deellink kon niet worden gemaakt (${response.status})`);
  }
  const path = `/share/${encodeURIComponent(key)}`;
  return typeof window === 'undefined' ? path : new URL(path, window.location.origin).toString();
};

export const resolveShareLinkApi = async (key: string): Promise<ResolvedShareLink | null> => {
  const baseUrl = getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');
  if (!baseUrl || !key) return null;
  const response = await authenticatedFetch(`${baseUrl}/api/v1/share-links/${encodeURIComponent(key)}`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Share link request failed (${response.status})`);
  const envelope = await response.json() as ShareEnvelope;
  const value = envelope.data?.value;
  if (envelope.ok === false || !value?.path) return null;
  const path = value.path;
  return {
    accessMode: value.accessMode || 'public',
    path,
    resourceType: value.resourceType || '',
    resourceId: value.resourceId || undefined,
  };
};

export const resolvePortalShareDestination = (share: ResolvedShareLink, shareKey?: string): string | null => {
  const shareQuery = shareKey ? `?share=${encodeURIComponent(shareKey)}` : '';
  if (share.resourceType === 'exercise' && share.resourceId) return `/training-library/exercises/${encodeURIComponent(share.resourceId)}${shareQuery}`;
  if (share.resourceType === 'plan' && share.resourceId) return `/training-library/plans/${encodeURIComponent(share.resourceId)}${shareQuery}`;
  if (share.resourceType === 'session' && share.resourceId) return `/training-library/sessions/${encodeURIComponent(share.resourceId)}${shareQuery}`;
  if (share.resourceType === 'viewer' && share.resourceId) {
    return `/trainer/viewer?exerciseId=${encodeURIComponent(share.resourceId)}`;
  }
  const parsed = new URL(share.path, 'https://portal.invalid');
  if (parsed.origin !== 'https://portal.invalid') return null;
  if (parsed.pathname === '/trainer/viewer' || parsed.pathname === '/viewer') {
    const exerciseId = parsed.searchParams.get('exerciseId');
    return exerciseId ? `/trainer/viewer?exerciseId=${encodeURIComponent(exerciseId)}` : null;
  }
  if (parsed.pathname.startsWith('/trainer/plans/')) {
    const planId = parsed.pathname.split('/')[3];
    return planId ? `/plans/${encodeURIComponent(planId)}/edit` : null;
  }
  if (parsed.pathname.startsWith('/plans/')) {
    const planId = parsed.pathname.split('/')[2];
    return planId ? `/plans/${encodeURIComponent(planId)}/edit` : null;
  }
  const listDestinations: Record<string, string> = {
    '/trainer': '/training-practices',
    '/trainer/exercises': '/training-practices',
    '/exercises': '/training-practices',
    '/trainer/plans': '/training-exercise-plans',
    '/plans': '/training-exercise-plans',
    '/trainer/sessions': '/training-sessions',
    '/sessions': '/training-sessions',
  };
  return listDestinations[parsed.pathname] || null;
};
